/**
 * Page Conduite : surveillant ou formateur saisit des observations sur un étudiant.
 */
import React, { useState } from 'react';
import {
  Award, Search, Plus, Minus, Save, X, Loader2,
} from 'lucide-react';
import {
  useListerConduiteQuery, useCreerConduiteMutation,
} from '../../services/vieScolaireApi';

const CATEGORIES = [
  { v: 'assiduite', label: 'Assiduité' },
  { v: 'discipline', label: 'Discipline' },
  { v: 'tenue', label: 'Tenue' },
  { v: 'comportement', label: 'Comportement en classe' },
  { v: 'travail', label: 'Travail / Investissement' },
  { v: 'respect', label: "Respect d'autrui" },
  { v: 'autre', label: 'Autre' },
] as const;

const NIVEAU_META: Record<string, { couleur: string; bg: string }> = {
  excellente:   { couleur: '#22c55e', bg: 'bg-green-50 dark:bg-green-900/30' },
  bonne:        { couleur: '#60a5fa', bg: 'bg-blue-50 dark:bg-blue-900/30' },
  passable:     { couleur: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-900/30' },
  insuffisante: { couleur: '#ef4444', bg: 'bg-red-50 dark:bg-red-900/30' },
  aucune:       { couleur: '#94a3b8', bg: 'bg-gray-50 dark:bg-gray-800/50' },
};

export default function ConduitePage() {
  const [matriculeInput, setMatriculeInput] = useState('');
  const [matricule, setMatricule] = useState('');
  const [showForm, setShowForm] = useState(false);

  const { data, isFetching } = useListerConduiteQuery(matricule, { skip: !matricule });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setMatricule(matriculeInput.trim().toUpperCase());
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Award size={24} /> Conduite des étudiants
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Observations positives ou négatives sur la conduite. Visibles par les parents et l'étudiant.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-4 flex gap-2">
        <Search size={18} className="text-gray-400 self-center" />
        <input value={matriculeInput} onChange={(e) => setMatriculeInput(e.target.value.toUpperCase())}
          placeholder="Saisir le matricule de l'étudiant (ex. 2I4252444)"
          className="flex-1 px-2 py-1.5 text-sm bg-transparent outline-none text-gray-900 dark:text-gray-100" />
        <button type="submit"
          className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">
          Rechercher
        </button>
      </form>

      {!matricule && (
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-12">
          Saisissez un matricule pour voir et compléter les observations.
        </div>
      )}

      {matricule && isFetching && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-blue-500" size={28} />
        </div>
      )}

      {matricule && data && (
        <>
          <div className={`rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4 flex items-center gap-4 ${NIVEAU_META[data.niveau.cle]?.bg || ''}`}>
            <Award size={36} strokeWidth={1.4} style={{ color: NIVEAU_META[data.niveau.cle]?.couleur }} />
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wide">
                {data.matricule}
              </div>
              <div className="text-2xl font-bold" style={{ color: NIVEAU_META[data.niveau.cle]?.couleur }}>
                {data.niveau.libelle}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                Score : {data.score > 0 ? `+${data.score}` : data.score} • {data.total} observation(s)
              </div>
            </div>
            <button onClick={() => setShowForm(true)}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-2">
              <Plus size={14} /> Nouvelle observation
            </button>
          </div>

          {data.results.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
              Aucune observation pour cet étudiant.
            </div>
          ) : (
            <div className="space-y-2">
              {data.results.map((e: any) => (
                <div key={e.id} className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 border-l-4 ${
                  e.type === 'positif' ? 'border-l-green-500' : 'border-l-red-500'
                }`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                        e.type === 'positif'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      }`}>
                        {e.type === 'positif' ? <Plus size={11} /> : <Minus size={11} />}
                        {e.categorie_label}
                      </span>
                      <span className="text-[10px] text-gray-500">Poids {e.poids}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(e.date_observation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                  {e.commentaire && (
                    <div className="text-sm text-gray-700 dark:text-gray-200 my-1.5 whitespace-pre-wrap">{e.commentaire}</div>
                  )}
                  <div className="text-[11px] text-gray-500">
                    Par {e.auteur_nom} ({e.auteur_role})
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && matricule && (
        <ModalCreer matricule={matricule} onClose={() => setShowForm(false)} />
      )}
    </div>
  );
}

function ModalCreer({ matricule, onClose }: { matricule: string; onClose: () => void }) {
  const [categorie, setCategorie] = useState<string>('comportement');
  const [type, setType] = useState<'positif' | 'negatif'>('negatif');
  const [poids, setPoids] = useState(1);
  const [commentaire, setCommentaire] = useState('');
  const [dateObs, setDateObs] = useState(new Date().toISOString().slice(0, 10));

  const [creer, { isLoading }] = useCreerConduiteMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentaire.trim()) return;
    try {
      await creer({
        matricule, categorie, type, poids,
        commentaire: commentaire.trim(),
        date_observation: dateObs,
      }).unwrap();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <form onSubmit={handle} className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="font-bold text-gray-900 dark:text-white">Nouvelle observation</h3>
          <button type="button" onClick={onClose} className="text-gray-500"><X size={18} /></button>
        </header>

        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setType('positif')}
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded inline-flex items-center justify-center gap-1.5 ${
                  type === 'positif'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 ring-2 ring-green-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}>
                <Plus size={14} /> Positif
              </button>
              <button type="button" onClick={() => setType('negatif')}
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded inline-flex items-center justify-center gap-1.5 ${
                  type === 'negatif'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-2 ring-red-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}>
                <Minus size={14} /> Négatif
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Catégorie</label>
              <select value={categorie} onChange={(e) => setCategorie(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Importance</label>
              <div className="flex gap-1">
                {[1, 2, 3].map(p => (
                  <button key={p} type="button" onClick={() => setPoids(p)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded ${
                      poids === p
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}>
                    {p === 1 ? 'Mineur' : p === 2 ? 'Moyen' : 'Majeur'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
            <input type="date" value={dateObs} onChange={(e) => setDateObs(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Commentaire</label>
            <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
              rows={4} maxLength={2000} required
              placeholder="Décrivez l'observation (ce qui sera visible par les parents et l'étudiant)"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !commentaire.trim()}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Enregistrer
          </button>
        </footer>
      </form>
    </div>
  );
}
